<template>
  <div
    class="constructor-step-card"
    :class="{ 'constructor-step-card--dragover': dragOver }"
    draggable="true"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <!-- head row -->
    <div
      class="constructor-step-row"
      :class="{ 'constructor-step-focused': focused, 'constructor-step-deleted': step.del }"
      @click="$emit('select'); $emit('toggle')"
    >
      <!-- drag handle -->
      <span
        class="constructor-step-drag-handle"
        @mousedown="dragEnabled = true"
        @click.stop
      >
        <feather-icon
          icon="MoreVerticalIcon"
          size="12"
        />
      </span>
      <feather-icon
        :icon="expanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
        size="12"
        class="constructor-step-toggle"
      />
      <feather-icon
        icon="ZapIcon"
        size="12"
        class="constructor-step-icon"
      />
      <span class="constructor-step-order">{{ step.ordernum }}.</span>

      <!-- name -->
      <b-form-input
        v-if="editingName"
        ref="nameInput"
        v-model="editNameValue"
        size="sm"
        class="constructor-step-name-input"
        @keydown.enter.prevent="saveName"
        @keydown.esc="cancelName"
        @blur="saveName"
        @click.native.stop
      />
      <span
        v-else
        class="constructor-step-name"
        @dblclick.stop="startEditName"
      >
        {{ step.name || '(без имени)' }}
      </span>
      <span class="constructor-step-oper">{{ step.oper }}</span>
      <span
        class="constructor-step-del-btn"
        :class="step.del ? 'constructor-step-del-btn--restore' : 'constructor-step-del-btn--delete'"
        :title="step.del ? 'Снять пометку удаления' : 'Пометить на удаление'"
        @click.stop="$emit('toggle-delete')"
      >
        <feather-icon
          :icon="step.del ? 'CornerUpLeftIcon' : 'TrashIcon'"
          size="12"
        />
      </span>
    </div>

    <!-- properties -->
    <template v-if="expanded">
      <div
        v-for="(prop, pi) in propKeys"
        :key="prop.key"
        class="constructor-step-prop"
        :class="{
          'constructor-step-focused': focusedPropIndex === pi,
          'constructor-step-prop--block': prop.editable === 'filter' || prop.editable === 'json',
        }"
        @click="$emit('select-prop', pi)"
      >
        <!-- prop title -->
        <span class="constructor-step-prop-key">{{ prop.key }}:</span>

        <cbs-inline-picker
          v-if="prop.editable === 'select' && prop.options !== 'objects'"
          :options="operandOptions"
          :display-val="prop.displayVal"
          label-key="text"
          placeholder="Поиск..."
          @select="selectOper(prop, $event)"
        />
        <cbs-inline-picker
          v-else-if="prop.editable === 'select' && prop.options === 'objects'"
          :options="objectRef"
          :display-val="prop.displayVal"
          label-key="title"
          placeholder="Поиск объекта..."
          @select="selectObject(prop, $event)"
        >
          <span
            v-if="prop.val"
            class="constructor-step-open-link"
            title="Открыть объект"
            @click.stop="openObjectTab(prop.val)"
          >
            <feather-icon
              icon="ExternalLinkIcon"
              size="12"
            />
          </span>
          <span
            v-if="prop.val"
            class="constructor-step-add-work"
            title="Добавить в рабочую область"
            @click.stop="$emit('add-work-object', prop.val)"
          >
            <feather-icon
              icon="PlusCircleIcon"
              size="12"
            />
          </span>
        </cbs-inline-picker>
        <template v-else-if="prop.editable === 'filter'">
          <span
            class="constructor-step-filter-toggle"
            @click.stop="toggleFilter"
          >
            <feather-icon
              :icon="filterExpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
              size="12"
            />
          </span>
          <span
            class="constructor-step-filter-summary"
            @click.stop="toggleFilter"
          >
            {{ filterSummary(prop.jsonObj) }}
          </span>
          <div
            v-if="filterExpanded"
            class="constructor-step-filter"
            @click.stop
          >
            <cbs-filter-constructor
              :node="prop.jsonObj.node"
              :fields="fields"
              :param-fields="paramFields"
              @save="saveJsonProp(prop)"
            />

            <!-- save button -->
            <div
              v-show="false"
              class="constructor-step-filter-actions"
            >
              <b-button
                size="sm"
                variant="outline-primary"
                @click="saveJsonProp(prop)"
              >
                <feather-icon
                  icon="SaveIcon"
                  size="12"
                />
                Сохранить фильтр
              </b-button>
            </div>
          </div>
        </template>
        <template v-else-if="prop.editable === 'json'">
          <span
            class="constructor-step-filter-toggle"
            @click.stop="$emit('toggle-json', prop.field)"
          >
            <feather-icon
              :icon="isJsonExpanded(prop.field) ? 'ChevronDownIcon' : 'ChevronRightIcon'"
              size="12"
            />
          </span>
          <span
            class="constructor-step-filter-summary"
            @click.stop="$emit('toggle-json', prop.field)"
          >{{ jsonSummary(prop) }}</span>
          <div
            v-if="isJsonExpanded(prop.field)"
            class="constructor-step-filter"
            @click.stop
          >
            <cbs-modification-constructor
              v-if="prop.renderType === 'jsonmodification'"
              :value="prop.jsonObj"
              :fields="destFields"
              :param-fields="sourceFields"
              @ensure-source-fields="$emit('load-object-fields', step.sourceobjectId)"
            />
            <cbs-groupby-constructor
              v-else-if="prop.renderType === 'jsongroupby'"
              :groupby="prop.jsonObj"
              :fields="destFields"
              :result-fields="destFields"
            />
            <cbs-join-constructor
              v-else-if="prop.renderType === 'jsonjoin'"
              :value="prop.jsonObj"
            />

            <!-- save button -->
            <div
              v-show="false"
              class="constructor-step-filter-actions"
            >
              <b-button
                size="sm"
                variant="outline-primary"
                @click="saveJsonProp(prop)"
              >
                <feather-icon
                  icon="SaveIcon"
                  size="12"
                />
                Сохранить
              </b-button>
            </div>
          </div>
        </template>
        <span
          v-else
          class="constructor-step-prop-val"
        >{{ prop.val }}</span>
      </div>
    </template>
  </div>
</template>

<script>
import { BFormInput, BButton } from 'bootstrap-vue'
import CbsFilterConstructor from '@/cubus/components/filter/CbsFilterConstructor.vue'
import CbsModificationConstructor from '@/cubus/components/modification/CbsModificationConstructor.vue'
import CbsGroupbyConstructor from '@/cubus/components/groupby/CbsGroupbyConstructor.vue'
import CbsJoinConstructor from '@/cubus/components/join/CbsJoinConstructor.vue'
import CbsInlinePicker from '@/cubus/components/inline-picker/CbsInlinePicker.vue'

export default {
  name: 'CbsConstructorStepCard',
  components: {
    BFormInput,
    BButton,
    CbsFilterConstructor,
    CbsModificationConstructor,
    CbsGroupbyConstructor,
    CbsJoinConstructor,
    CbsInlinePicker,
  },
  props: {
    step: {
      type: Object,
      required: true,
    },
    stepIdx: {
      type: Number,
      required: true,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    focused: {
      type: Boolean,
      default: false,
    },
    focusedPropIndex: {
      type: Number,
      default: -1,
    },
    propKeys: {
      type: Array,
      default: () => [],
    },
    operandOptions: {
      type: Array,
      default: () => [],
    },
    objectRef: {
      type: Array,
      default: () => [],
    },
    fields: {
      type: Array,
      default: () => [],
    },
    destFields: {
      type: Array,
      default: () => [],
    },
    sourceFields: {
      type: Array,
      default: () => [],
    },
    paramFields: {
      type: Array,
      default: () => [],
    },
    filterExpanded: {
      type: Boolean,
      default: false,
    },
    jsonExpanded: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      dragOver: false,
      dragEnabled: false,
      editingName: false,
      editNameValue: '',
    }
  },
  watch: {
    focusedPropIndex(newIdx, oldIdx) {
      if (oldIdx >= 0 && oldIdx < this.propKeys.length) {
        const prev = this.propKeys[oldIdx]
        if (prev && (prev.editable === 'filter' || prev.editable === 'json')) {
          this.saveJsonProp(prev)
        }
      }
    },
  },
  mounted() {
    this._resetDrag = () => { this.dragEnabled = false }
    document.addEventListener('mouseup', this._resetDrag)
  },
  beforeDestroy() {
    document.removeEventListener('mouseup', this._resetDrag)
  },
  methods: {
    startEditName() {
      this.editNameValue = this.step.name || ''
      this.editingName = true
      this.$nextTick(() => {
        const input = this.$refs.nameInput
        if (input) input.focus()
      })
    },
    saveName() {
      if (!this.editingName) return
      this.editingName = false
      const val = this.editNameValue.trim()
      if (val && val !== this.step.name) {
        this.$emit('update-field', { field: 'name', value: val })
      }
    },
    cancelName() {
      this.editingName = false
    },
    filterSummary(filterObj) {
      if (!filterObj || !filterObj.node) return '(пусто)'
      return this.nodeSummary(filterObj.node)
    },
    nodeSummary(node) {
      if (!node) return ''
      const operName = (node.oper && node.oper.sid === 'or') ? 'ИЛИ' : 'И'
      const parts = []
      if (node.conds && node.conds.length) {
        node.conds.forEach(c => {
          if (!c.isactive) return
          parts.push(this.condSummary(c))
        })
      }
      if (node.nodes && node.nodes.length) {
        node.nodes.forEach(n => {
          if (!n.isactive) return
          parts.push(this.nodeSummary(n))
        })
      }
      if (!parts.length) return '(пусто)'
      if (parts.length === 1) return parts[0]
      return `${operName}(${parts.join(', ')})`
    },
    condSummary(cond) {
      if (!cond || !cond.args || !cond.args.length) return '?'
      const operName = (cond.oper && cond.oper.name) || '?'
      const arg0 = this.argSummary(cond.args[0])
      if (cond.args.length < 2 || (cond.oper && cond.oper.args === 1)) {
        return `${arg0} ${operName}`
      }
      const arg1 = this.argSummary(cond.args[1])
      return `${arg0} ${operName} ${arg1}`
    },
    argSummary(arg) {
      if (!arg) return '?'
      const t = arg.type && arg.type.sid
      if (t === 'entity' || t === 'paramentity' || t === 'sourceentity') {
        const fld = this.fields.find(f => (arg.entitysid && f.entitysid === arg.entitysid)
          || (arg.value != null && f.value === arg.value))
        if (fld) return fld.title || fld.entitysid || String(fld.value)
        if (arg.entitysid) return arg.entitysid
        return arg.value != null ? String(arg.value) : '?'
      }
      if (t === 'value') {
        if (arg.value === true) return 'ИСТИНА'
        if (arg.value === false) return 'ЛОЖЬ'
        if (arg.value == null || arg.value === '') return '?'
        return String(arg.value)
      }
      if (arg.value != null) return String(arg.value)
      return '?'
    },
    selectObject(prop, obj) {
      this.$emit('update-field', { field: prop.field, value: obj.value, title: obj.title })
    },
    selectOper(prop, op) {
      this.$emit('update-field', { field: prop.field, value: op.value })
    },
    openObjectTab(objectId) {
      const route = this.$router.resolve({ name: 'cubus-object', params: { objectid: objectId } })
      window.open(route.href, '_blank')
    },
    toggleFilter() {
      this.$emit('toggle-filter')
    },
    isJsonExpanded(field) {
      return !!this.jsonExpanded[field]
    },
    jsonSummary(prop) {
      const obj = prop.jsonObj
      if (!obj || !Object.keys(obj).length) return '(пусто)'
      if (prop.renderType === 'jsonmodification') {
        if (!obj.list || !obj.list.length) return '(пусто)'
        return `${obj.list.length} модификаций`
      }
      if (prop.renderType === 'jsongroupby') {
        if (!obj.groups || !obj.groups.length) return '(пусто)'
        return `${obj.groups.length} группировок`
      }
      if (prop.renderType === 'jsonjoin') {
        if (!obj.joins || !obj.joins.length) return '(пусто)'
        return `${obj.joins.length} соединений`
      }
      return JSON.stringify(obj).substring(0, 50)
    },
    saveJsonProp(prop) {
      console.log('CbsConstructorStepCard : saveJsonProp', prop)
      this.$emit('update-field', { field: prop.field, value: prop.jsonObj })
    },
    onDragStart(e) {
      if (!this.dragEnabled) {
        e.preventDefault()
        return
      }
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(this.stepIdx))
      this.$emit('drag-start', this.stepIdx)
    },
    onDragEnd() {
      this.dragEnabled = false
      this.$emit('drag-end')
    },
    onDragOver() {
      this.dragOver = true
    },
    onDragLeave(e) {
      if (!this.$el.contains(e.relatedTarget)) {
        this.dragOver = false
      }
    },
    onDrop(e) {
      this.dragOver = false
      const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10)
      if (!Number.isNaN(fromIdx) && fromIdx !== this.stepIdx) {
        this.$emit('reorder', { fromIdx, toIdx: this.stepIdx })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.constructor-step-row {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 0;
  font-size: 14px;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #f8f8f8;
  }
}

.constructor-step-card--dragover {
  border-top: 2px solid #7367f0;
}

.constructor-step-drag-handle {
  color: #d0d0d0;
  flex-shrink: 0;
  cursor: grab;

  &:hover {
    color: #7367f0;
  }

  &:active {
    cursor: grabbing;
  }
}

.constructor-step-toggle {
  color: #b9b9c3;
  flex-shrink: 0;
}

.constructor-step-icon {
  color: #ff9f43;
  flex-shrink: 0;
}

.constructor-step-order {
  color: #b9b9c3;
  font-size: 13px;
  min-width: 16px;
}

.constructor-step-name {
  color: #5e5873;
  font-weight: 500;
  cursor: default;
}

.constructor-step-name-input {
  font-size: 13px;
  font-weight: 500;
  height: 22px;
  padding: 0 4px;
  max-width: 200px;
}

.constructor-step-oper {
  color: #b9b9c3;
  font-size: 13px;
}

.constructor-step-prop {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 0;
  padding-left: 22px;
  font-size: 12px;
  border-radius: 4px;
}

.constructor-step-prop-key {
  color: #b9b9c3;
  min-width: 30px;
  flex-shrink: 0;
}

.constructor-step-prop-val {
  color: #6e6b7b;
}

.constructor-step-select {
  flex: 1;
  font-size: 12px;
  height: 22px;
  padding: 0 4px;
  max-width: 200px;
}

.constructor-step-open-link {
  color: #b9b9c3;
  cursor: pointer;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;
  flex-shrink: 0;

  &:hover {
    color: #7367f0;
    background: rgba(115, 103, 240, 0.1);
  }
}

.constructor-step-add-work {
  color: #b9b9c3;
  cursor: pointer;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;
  flex-shrink: 0;

  &:hover {
    color: #28c76f;
    background: rgba(40, 199, 111, 0.1);
  }
}

.constructor-step-prop--block {
  flex-wrap: wrap;
}

.constructor-step-filter {
  width: 100%;
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  margin-top: 2px;
  padding: 4px 6px;
}

.constructor-step-filter-toggle {
  color: #b9b9c3;
  flex-shrink: 0;
  cursor: pointer;
}

.constructor-step-filter-summary {
  color: #6e6b7b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  user-select: none;
}

.constructor-step-filter-actions {
  padding: 4px 8px;
  display: flex;
  justify-content: flex-end;
}

.constructor-step-focused {
  background: rgba(115, 103, 240, 0.08);
  outline: 1px solid rgba(115, 103, 240, 0.3);
}

.constructor-step-deleted {
  opacity: 0.5;

  .constructor-step-name,
  .constructor-step-oper,
  .constructor-step-order {
    text-decoration: line-through;
  }
}

.constructor-step-del-btn {
  margin-left: auto;
  cursor: pointer;
  color: #b9b9c3;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;
  flex-shrink: 0;
  visibility: hidden;

  .constructor-step-row:hover & {
    visibility: visible;
  }
}

.constructor-step-deleted .constructor-step-del-btn {
  visibility: visible;
}

.constructor-step-del-btn--delete:hover {
  color: #ea5455;
  background: rgba(234, 84, 85, 0.1);
}

.constructor-step-del-btn--restore:hover {
  color: #28c76f;
  background: rgba(40, 199, 111, 0.1);
}

// Dark mode
.dark-layout {
  .constructor-step-drag-handle {
    color: #4b4b5a;

    &:hover {
      color: #7367f0;
    }
  }

  .constructor-step-row:hover {
    background: #3b4253;
  }

  .constructor-step-name {
    color: #d0d2d6;
  }

  .constructor-step-prop-val {
    color: #b4b7bd;
  }

  .constructor-step-focused {
    background: rgba(115, 103, 240, 0.15);
    outline-color: rgba(115, 103, 240, 0.4);
  }

  .constructor-step-del-btn {
    color: #4b4b5a;
  }

  .constructor-step-filter {
    border-color: #3b4253;
  }

  .constructor-step-filter-summary {
    color: #b4b7bd;
  }

}
</style>
